/**
 * traffic-source.js
 * -----------------
 * Shared traffic-source / attribution detection utility for the EasyRider storefront.
 *
 * Responsibilities:
 *   1. Parse UTM params from the current URL.
 *   2. Normalize utm_source into a small fixed set of channels via SOURCE_MAP.
 *   3. Fall back to document.referrer when no UTM params are present.
 *   4. Persist attribution to localStorage using a configurable first-touch /
 *      last-touch model with a rolling attribution window.
 *   5. Expose window.TrafficSource.get() and window.TrafficSource.getChannel().
 *
 * Must load FIRST (before analytics-events.js and amazon-attribution.js).
 *
 * Safe with zero live campaigns: no UTM + no external referrer resolves to a
 * valid `direct` record. Never throws, never returns an empty object.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Config — edit here to change behaviour without touching call sites.
   * ------------------------------------------------------------------ */
  var CONFIG = {
    // 'first-touch' keeps the earliest known touch for the window's duration.
    // 'last-touch' overwrites on every *meaningful* touch (see isMeaningful()).
    attributionModel: 'first-touch',

    // Days after firstTouchAt before the stored record is treated as stale
    // and discarded on the next visit.
    attributionWindowDays: 30,

    // localStorage key holding the single JSON attribution blob.
    storageKey: 'er_attribution',

    // Bump when the stored shape changes; old-version records are discarded.
    schemaVersion: 1,
  };

  /* ------------------------------------------------------------------ *
   * Channel mapping.
   * Keys are lower-cased utm_source values AND referrer hostnames.
   * Add new channels (e.g. tiktok) by adding rows here — one line each.
   * An unmapped utm_source resolves to 'other'; no signal at all -> 'direct'.
   * ------------------------------------------------------------------ */
  var SOURCE_MAP = {
    // Instagram
    instagram: 'instagram',
    ig: 'instagram',
    'instagram.com': 'instagram',
    'l.instagram.com': 'instagram',
    // Facebook / Meta
    facebook: 'facebook',
    fb: 'facebook',
    meta: 'facebook',
    'facebook.com': 'facebook',
    'm.facebook.com': 'facebook',
    'l.facebook.com': 'facebook',
    'lm.facebook.com': 'facebook',
    // Google
    google: 'google',
    adwords: 'google',
    googleads: 'google',
    'google.com': 'google',
    'www.google.com': 'google',
    // Twitter / X — external but not a first-class channel yet.
    't.co': 'other',
    twitter: 'other',
    x: 'other',
  };

  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  /* ------------------------------------------------------------------ *
   * localStorage helpers — tolerant of private mode / disabled storage.
   * Falls back to an in-memory store so the API keeps working.
   * ------------------------------------------------------------------ */
  var memoryStore = {};

  function readRaw() {
    try {
      return window.localStorage.getItem(CONFIG.storageKey);
    } catch (e) {
      return Object.prototype.hasOwnProperty.call(memoryStore, CONFIG.storageKey)
        ? memoryStore[CONFIG.storageKey]
        : null;
    }
  }

  function writeRaw(value) {
    try {
      window.localStorage.setItem(CONFIG.storageKey, value);
    } catch (e) {
      memoryStore[CONFIG.storageKey] = value;
    }
  }

  /* ------------------------------------------------------------------ *
   * Parsing.
   * ------------------------------------------------------------------ */
  function parseUtm() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return {};
    }
    var out = {};
    for (var i = 0; i < UTM_KEYS.length; i++) {
      var key = UTM_KEYS[i];
      var val = params.get(key);
      out[key] = val && val.trim() ? val.trim() : null;
    }
    return out;
  }

  function normalizeChannel(source) {
    if (!source) return null;
    var key = String(source).trim().toLowerCase();
    if (Object.prototype.hasOwnProperty.call(SOURCE_MAP, key)) {
      return SOURCE_MAP[key];
    }
    // A real but unrecognised utm_source is still a known *external* touch.
    return 'other';
  }

  function referrerHostname() {
    if (!document.referrer) return null;
    try {
      return new URL(document.referrer).hostname.toLowerCase();
    } catch (e) {
      return null;
    }
  }

  // Same-origin referrer = internal navigation = NOT a new source.
  function isSameOrigin(hostname) {
    if (!hostname) return false;
    try {
      return hostname === window.location.hostname.toLowerCase();
    } catch (e) {
      return false;
    }
  }

  function channelFromReferrer(hostname) {
    if (!hostname) return null;
    // Exact host match first, then progressively strip subdomains so
    // e.g. "story.instagram.com" still resolves via "instagram.com".
    var parts = hostname.split('.');
    for (var i = 0; i < parts.length - 1; i++) {
      var candidate = parts.slice(i).join('.');
      if (Object.prototype.hasOwnProperty.call(SOURCE_MAP, candidate)) {
        return SOURCE_MAP[candidate];
      }
    }
    return null;
  }

  /* ------------------------------------------------------------------ *
   * Detect the attribution for THIS visit (before merge with stored).
   * Fallback order: UTM -> referrer -> direct.
   * ------------------------------------------------------------------ */
  function detectCurrent() {
    var utm = parseUtm();
    var now = Date.now();
    var landingUrl = window.location.href;

    // 1. UTM present — highest confidence.
    if (utm.utm_source) {
      return {
        v: CONFIG.schemaVersion,
        source: utm.utm_source,
        medium: utm.utm_medium,
        campaign: utm.utm_campaign,
        content: utm.utm_content,
        term: utm.utm_term,
        channel: normalizeChannel(utm.utm_source),
        firstTouchAt: now,
        landingUrl: landingUrl,
        resolvedFrom: 'utm',
      };
    }

    // 2. Referrer — best guess when no UTM.
    var host = referrerHostname();
    if (host && !isSameOrigin(host)) {
      var refChannel = channelFromReferrer(host);
      if (refChannel) {
        return {
          v: CONFIG.schemaVersion,
          source: host,
          medium: 'referral',
          campaign: null,
          content: null,
          term: null,
          channel: refChannel,
          firstTouchAt: now,
          landingUrl: landingUrl,
          resolvedFrom: 'referrer',
        };
      }
    }

    // 3. Direct — the honest "we don't know" bucket. Still a valid record.
    return {
      v: CONFIG.schemaVersion,
      source: null,
      medium: null,
      campaign: null,
      content: null,
      term: null,
      channel: 'direct',
      firstTouchAt: now,
      landingUrl: landingUrl,
      resolvedFrom: 'direct',
    };
  }

  // A "meaningful" touch carries a real external signal (UTM, or a recognised
  // referrer). A plain direct visit is NOT meaningful and must never clobber a
  // prior real touch under last-touch, nor upgrade a record under first-touch.
  function isMeaningful(record) {
    return record.resolvedFrom === 'utm' || record.resolvedFrom === 'referrer';
  }

  function isStale(record) {
    if (!record || typeof record.firstTouchAt !== 'number') return true;
    var windowMs = CONFIG.attributionWindowDays * 24 * 60 * 60 * 1000;
    return Date.now() - record.firstTouchAt > windowMs;
  }

  function readStored() {
    var raw = readRaw();
    if (!raw) return null;
    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return null; // corrupt — treat as absent.
    }
    if (!parsed || parsed.v !== CONFIG.schemaVersion) return null; // wrong schema.
    if (isStale(parsed)) return null; // outside attribution window.
    return parsed;
  }

  /* ------------------------------------------------------------------ *
   * Merge current visit with stored record per the configured model,
   * then persist. Returns the effective record.
   * ------------------------------------------------------------------ */
  function resolve() {
    var current = detectCurrent();
    var stored = readStored();

    // No valid stored record — current visit becomes the record.
    if (!stored) {
      writeRaw(JSON.stringify(current));
      return current;
    }

    if (CONFIG.attributionModel === 'last-touch') {
      // Overwrite only on a meaningful touch; preserve firstTouchAt as the
      // window anchor so the record still expires 30 days after the FIRST touch.
      if (isMeaningful(current)) {
        current.firstTouchAt = stored.firstTouchAt;
        writeRaw(JSON.stringify(current));
        return current;
      }
      return stored;
    }

    // first-touch (default): keep the stored record, with one upgrade —
    // if the stored record is an unknown 'direct' and the current visit is
    // meaningful, adopt the current channel (but keep the original timestamp).
    if (stored.resolvedFrom === 'direct' && isMeaningful(current)) {
      current.firstTouchAt = stored.firstTouchAt;
      writeRaw(JSON.stringify(current));
      return current;
    }

    return stored;
  }

  /* ------------------------------------------------------------------ *
   * Public API.
   * ------------------------------------------------------------------ */
  var record;
  try {
    record = resolve();
  } catch (e) {
    // Absolute last resort — never let attribution break the page.
    record = {
      v: CONFIG.schemaVersion,
      source: null,
      medium: null,
      campaign: null,
      content: null,
      term: null,
      channel: 'direct',
      firstTouchAt: Date.now(),
      landingUrl: window.location.href,
      resolvedFrom: 'direct',
    };
  }

  window.TrafficSource = {
    /**
     * @returns {{source,medium,campaign,content,term,channel,firstTouchAt,landingUrl,resolvedFrom,v}}
     *          A shallow copy so callers can't mutate the internal record.
     */
    get: function () {
      var copy = {};
      for (var k in record) {
        if (Object.prototype.hasOwnProperty.call(record, k)) copy[k] = record[k];
      }
      return copy;
    },

    /** Shorthand for the normalized channel. Always a non-empty string. */
    getChannel: function () {
      return (record && record.channel) || 'direct';
    },

    /** Exposed for debugging / tests. */
    _config: CONFIG,
  };
})();
